import { styled } from '@f8n-frontend/stitches';
import { keepPreviousData } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState, memo } from 'react';
import { useClickAway } from 'react-use';

import Box from 'components/base/Box';
import { HeaderProps } from 'components/headers/types';

import useGlobalSearch from 'hooks/queries/use-global-search';

import { PageColorMode } from 'types/page';

import SearchBarInput from './SearchBarInput';
import SearchBarOverlay from './SearchBarOverlay';
import SearchBarResults from './SearchBarResults';

const SearchContainer = styled(Box, {
  flex: 1,
  position: 'relative',
  justifyContent: 'center',
  marginRight: '$3',
  '@bp2': {
    marginRight: 0,
    maxWidth: '$searchInput',
  },
  '@bp3': {
    width: '$searchInput',
  },
});

interface SearchBarProps extends HeaderProps {
  pageColorMode: PageColorMode;
}

export default memo(SearchBar);

function SearchBar(props: SearchBarProps) {
  const { pageColorMode, searchOpen } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1_000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const globalSearchQuery = useGlobalSearch(
    {
      searchTerm: debouncedSearchTerm,
    },
    {
      placeholderData: keepPreviousData,
      enabled: isFocused && debouncedSearchTerm.length > 1,
    }
  );

  const router = useRouter();

  useEffect(() => {
    setIsFocused(false);
  }, [router.asPath]);

  const ref = useRef(null);

  useClickAway(ref, () => {
    setIsFocused(false);
  });

  return (
    <>
      <SearchContainer
        ref={ref}
        css={{
          display: searchOpen ? 'block' : 'none',
          '@bp2': {
            display: 'block',
          },
        }}
      >
        <SearchBarInput
          colorMode={pageColorMode}
          isFocused={isFocused}
          placeholder="Search galleries, exhibitions, artists or works"
          searchOpen={searchOpen}
          searchTerm={searchTerm}
          setIsFocused={setIsFocused}
          setSearchTerm={setSearchTerm}
          title="Search galleries, exhibitions, artists or works"
          isLoading={
            (globalSearchQuery.isLoading ||
              debouncedSearchTerm !== searchTerm) &&
            searchTerm.length > 1
          }
        />
        <Box css={{ position: 'relative' }}>
          {isFocused && searchTerm && globalSearchQuery.isSuccess && (
            <SearchBarResults
              currentValue={searchTerm}
              results={globalSearchQuery.data}
              isLoading={globalSearchQuery.isLoading}
            />
          )}
        </Box>
      </SearchContainer>
      <SearchBarOverlay
        css={{
          transform: 'translate3d(0, 0, 0)',
          opacity: isFocused ? 1 : 0,
          pointerEvents: isFocused ? 'all' : 'none',
          display: 'none',
          '@bp2': {
            display: 'block',
          },
        }}
      />
    </>
  );
}
